import React, { useEffect, useRef, useState } from "react";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";

const GOOGLE_SCRIPT_ID = "google-identity-service";

const parseCredential = (credential) => {
  const parts = credential.split(".");
  if (parts.length < 2) {
    throw new Error("Invalid Google credential.");
  }
  const payload = parts[1]
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .padEnd(Math.ceil(parts[1].length / 4) * 4, "=");
  return JSON.parse(window.atob(payload));
};

const GoogleSignInButton = ({ onSuccess, onError, label }) => {
  const { loginWithToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const buttonContainerRef = useRef(null);
  const isMountedRef = useRef(true);
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    isMountedRef.current = true;
    if (!clientId) return;

    let loadTarget = null;
    let loadHandler = null;
    const container = buttonContainerRef.current;

    const initializeGoogle = () => {
      if (!window.google?.accounts?.id || !container) return;

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response) => {
          try {
            if (!isMountedRef.current) return;
            setLoading(true);
            const profile = parseCredential(response.credential);
            const apiResponse = await api.post("/auth/google/", {
              email: profile.email,
              first_name: profile.given_name || "",
              last_name: profile.family_name || "",
              picture: profile.picture || "",
            });
            if (!isMountedRef.current) return;
            loginWithToken(apiResponse.data);
            onSuccess?.(apiResponse.data);
          } catch (error) {
            if (!isMountedRef.current) return;
            onError?.(error?.response?.data?.detail || "Google sign-in failed. Please try again.");
          } finally {
            if (isMountedRef.current) {
              setLoading(false);
            }
          }
        },
      });

      container.replaceChildren();
      window.google.accounts.id.renderButton(container, {
        theme: "outline",
        size: "large",
        width: container.offsetWidth || 320,
        text: "continue_with",
        shape: "pill",
      });
      if (isMountedRef.current) {
        setReady(true);
      }
    };

    if (window.google?.accounts?.id) {
      initializeGoogle();
      return () => {
        isMountedRef.current = false;
        if (container) {
          container.replaceChildren();
        }
      };
    }

    const existing = document.getElementById(GOOGLE_SCRIPT_ID);
    if (existing) {
      loadTarget = existing;
      loadHandler = initializeGoogle;
      existing.addEventListener("load", loadHandler, { once: true });
      return () => {
        isMountedRef.current = false;
        if (loadTarget && loadHandler) {
          loadTarget.removeEventListener("load", loadHandler);
        }
        if (container) {
          container.replaceChildren();
        }
      };
    }

    const script = document.createElement("script");
    script.id = GOOGLE_SCRIPT_ID;
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = initializeGoogle;
    document.body.appendChild(script);
    return () => {
      isMountedRef.current = false;
      if (container) {
        container.replaceChildren();
      }
    };
  }, [clientId, loginWithToken, onError, onSuccess]);

  if (!clientId) {
    return (
      <button disabled className="w-full h-14 rounded-full border border-gray-100 bg-gray-50 text-gray-300 font-bold text-xs uppercase tracking-widest cursor-not-allowed">
        Google Sync Unavailable
      </button>
    );
  }

  return (
    <div className="w-full relative group">
      {label && (
        <div className="mb-4 text-[10px] font-black text-theme-muted uppercase tracking-[0.3em] text-center">
          {label}
        </div>
      )}
      <div className="relative min-h-[56px] flex items-center justify-center">
        <div
          ref={buttonContainerRef}
          className={`w-full transition-all duration-500 ${ready ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}
        />
        {loading && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center z-10 animate-fade-in border border-component-border">
            <div className="w-5 h-5 border-2 border-chafadi-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {!ready && !loading && (
           <div className="absolute inset-0 bg-theme-bg rounded-full flex items-center justify-center border border-component-border border-dashed animate-pulse">
             <span className="text-[10px] font-black text-theme-muted uppercase tracking-widest">Warming Engine...</span>
           </div>
        )}
      </div>
    </div>
  );
};

export default GoogleSignInButton;
